import { useState, useEffect } from 'react';
import BackButton from '../components/BackButton';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function MyContracts() {
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();
  const [workContracts, setWorkContracts] = useState([]);
  const [gatewayContract, setGatewayContract] = useState(null);
  const [error, setError] = useState(null);
  const [showSignForm, setShowSignForm] = useState(false);
  const [company, setCompany] = useState(null);
  
  // Sign form state
  const [contractType, setContractType] = useState('umowa_zlecenie');
  const [role, setRole] = useState('');
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [workRes, gwRes, compRes] = await Promise.all([
        api.get('/work-contract/my').catch(() => ({ data: [] })),
        api.get('/gateway/my').catch(() => ({ data: null })),
        api.get('/me/company').catch(() => ({ data: null })),
      ]);
      setWorkContracts(workRes.data || []);
      // API returns array, take first contract
      const gwData = Array.isArray(gwRes.data) ? gwRes.data[0] || null : gwRes.data;
      setGatewayContract(gwData);
      setCompany(compRes.data);
    } catch (e) {
      setError(e.userMessage || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (type, id) => {
    try {
      const url = type === 'gateway' ? `/gateway/${id}/pdf` : `/work-contract/${id}/pdf`;
      const response = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${type}_contract_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      alert('Ошибка скачивания: ' + (e.userMessage || e.message));
    }
  };

  const handleSign = async () => {
    if (!role.trim()) {
      alert('Укажите должность');
      return;
    }
    setSigning(true);
    try {
      await api.post('/work-contract/sign', {
        company_id: company?.id,
        contract_type: contractType,
        role_text: role,
        kup_50_enabled: contractType === 'umowa_o_dzielo',
      });
      alert('✅ Договор подписан!');
      setShowSignForm(false);
      setRole('');
      loadData();
    } catch (e) {
      alert('Ошибка: ' + (e.userMessage || e.message));
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return <div className="page">
      <BackButton /><div className="loader">{t('common.loading')}</div></div>;
  }

  const typeLabels = {
    umowa_o_prace: '💼 Umowa o pracę',
    umowa_zlecenie: '📋 Umowa zlecenie',
    umowa_o_dzielo_50: '🎨 Umowa o dzieło (50% KUP)',
    umowa_o_dzielo_20: '🎨 Umowa o dzieło (20% KUP)',
    umowa_o_dzielo: '🎨 Umowa o dzieło',
    umowa_b2b: '🏢 Umowa B2B',
    umowa_okres_probny: '⏳ Umowa na okres próbny',
    umowa_osoba_fizyczna: '👤 Umowa z osobą fizyczną',
    manager: '👔 Manager',
  };

  return (
    <div className="page">
      <BackButton />
      <h1>{t('my_contracts.title')}</h1>
      
      {error && <div className="error-box">{error}</div>}

      {/* Gateway Contract */}
      {gatewayContract && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12 }}>🚀 Gateway договор</h3>
          <div className="list-item" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
            <div className="list-main">
              <div className="list-title">Gateway Contract</div>
              <div className="list-sub">{company?.name} • {gatewayContract.signed_at?.split('T')[0]}</div>
            </div>
            <button 
              className="btn btn-secondary"
              style={{ padding: '8px 12px' }}
              onClick={() => downloadPdf('gateway', gatewayContract.id)}
            >
              📥 PDF
            </button>
          </div>
        </div>
      )}

      {/* Work Contracts */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>📝 Рабочие договоры</h3>
          <button 
            className="btn btn-primary"
            style={{ padding: '8px 16px' }}
            onClick={() => setShowSignForm(!showSignForm)}
          >
            {showSignForm ? '✕ Отмена' : '+ Подписать'}
          </button>
        </div>

        {/* Sign Form */}
        {showSignForm && (
          <div style={{ 
            background: 'rgba(255,255,255,0.05)', 
            padding: 16, 
            borderRadius: 12, 
            marginBottom: 16 
          }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Тип договора:</label>
              <select 
                value={contractType} 
                onChange={e => setContractType(e.target.value)}
                style={{ width: '100%', padding: 10, borderRadius: 6, background: '#1a1a2e', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <option value="umowa_o_prace">💼 Umowa o pracę (трудовой)</option>
                <option value="umowa_zlecenie">📋 Umowa zlecenie (услуга)</option>
                <option value="umowa_o_dzielo_50">🎨 Umowa o dzieło (50% KUP)</option>
                <option value="umowa_o_dzielo_20">🎨 Umowa o dzieło (20% KUP)</option>
                <option value="umowa_b2b">🏢 Umowa B2B</option>
                <option value="umowa_okres_probny">⏳ Umowa na okres próbny</option>
                <option value="umowa_osoba_fizyczna">👤 Umowa z osobą fizyczną</option>
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, opacity: 0.7 }}>Должность:</label>
              <input 
                type="text"
                value={role}
                onChange={e => setRole(e.target.value)}
                placeholder="например: Content Creator"
                style={{ width: '100%', padding: 10, borderRadius: 6, background: '#1a1a2e', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
              />
            </div>
            <button 
              className="btn btn-primary"
              style={{ width: '100%' }}
              onClick={handleSign}
              disabled={signing}
            >
              {signing ? '⏳ Подписываем...' : '✍️ Подписать договор'}
            </button>
          </div>
        )}

        {/* Work Contracts List */}
        {workContracts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, opacity: 0.6 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📝</div>
            <p>Нет рабочих договоров</p>
          </div>
        ) : (
          <div className="list">
            {workContracts.map(c => (
              <div key={c.id} className="list-item">
                <div className="list-main">
                  <div className="list-title">{typeLabels[c.contract_type] || c.contract_type}</div>
                  <div className="list-sub">{c.role}</div>
                  <div className="list-sub" style={{ opacity: 0.5 }}>{c.signed_at?.split('T')[0]}</div>
                </div>
                <button 
                  className="btn btn-secondary"
                  style={{ padding: '8px 12px' }}
                  onClick={() => downloadPdf('work', c.id)}
                >
                  📥 PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
